import React, { useState } from "react";
import { useEffect } from "react";
import Header from "./Header";
import Menu from "./Menu";
import Cart from "./Cart";
import Checkout from "./Checkout";
import OrderTracking from "./OrderTracking";
import CustomerService from "./CustomerService";
import LegalPages from "./LegalPages";
import ScrollToTop from "./ScrollToTop";
import Footer from "./Footer";
import DevTools from "./DevTools";
import OrderHistory from "./OrderHistory";
import Profile from "./Profile"; // Import the Profile component
import UpdateProfile from "./UpdateProfile";
import OrderDetail from "./OrderDetail"; // Import OrderDetail
import About from "./About";
import { useTranslation } from "react-i18next";
import {
  CartItem,
  MenuItem,
  User,
  LoginResponse,
  Address,
  LocationSettings,
} from "../types";
import { deleteOrder, Order } from "../api/order";
import { getLocationSettings } from "../api/settings";
import { getItem, removeItem } from "../utils/localStorage";

interface MainAppProps {
  user: LoginResponse | null;
  onOpenAuth: (
    page:
      | "menu"
      | "cart"
      | "checkout"
      | "tracking"
      | "service"
      | "legal"
      | "profile"
      | "update-profile"
  ) => void;
  onLogout: () => void;
}

const MainApp: React.FC<MainAppProps> = ({ user, onOpenAuth, onLogout }) => {
  const [currentPage, setCurrentPage] = useState<
    | "menu"
    | "cart"
    | "checkout"
    | "tracking"
    | "service"
    | "legal"
    | "profile"
    | "update-profile"
    | "order-detail"
    | "order-history"
    | "about"
  >("menu");
  const [legalPageType, setLegalPageType] = useState<
    "privacy" | "terms" | "refund" | "legal"
  >("privacy");
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null); // Add state for selected order
  const { t } = useTranslation();
  const [locationSettings, setLocationSettings] =
    useState<LocationSettings | null>(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    const fetchLocationSettings = async () => {
      const { data, error } = await getLocationSettings();
      console.log("fetchLocationSettings", data);
      if (data) {
        setLocationSettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
      }
    };

    fetchLocationSettings();
  }, [t]);

  useEffect(() => {
    return () => {
      if (currentPage === "checkout") {
        const currentOrderID = getItem("currentOrderID");

        if (currentOrderID && !paymentSucceeded) {
          console.log(
            `Cleanup initiated by MainApp: Unpaid order ${currentOrderID} being deleted.`
          );

          deleteOrder(currentOrderID as string)
            .then(() => {
              console.log(`Order ${currentOrderID} deleted successfully.`);
            })
            .catch((error) => {
              console.error(
                `Failed to delete order ${currentOrderID} from MainApp:`,
                error
              );
            })
            .finally(() => {
              removeItem("currentOrderID");
            });
        } else if (paymentSucceeded) {
          console.log("Cleanup skipped: Payment succeeded.");
          removeItem("currentOrderID"); // Sécurité
        }
      }
    };
  }, [currentPage, paymentSucceeded]); // Dépendances :currentPage change (navigation) et paymentSucceeded change (résultat du paiement)

  const addToCart = (item: Omit<MenuItem, "quantity">) => {
    setCartItems((prev) => {
      const existingItem = prev.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prev.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });

    // Déclencher l'animation du panier
    setTimeout(() => {
      const cartButton = document.querySelector("[data-cart-button]");
      if (cartButton) {
        cartButton.classList.add("animate-cart-bounce");
        setTimeout(() => {
          cartButton.classList.remove("animate-cart-bounce");
        }, 800);
      }
    }, 100);
  };

  const removeFromCart = (id: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const getTotalPrice = () => {
    return cartItems.reduce(
      (total, item) => total + item.dish.price * item.quantity,
      0
    );
  };

  const getCartItemsCount = () => {
    return cartItems.length;
    //return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = () => {
    // Cette fonction sera maintenant appelée depuis Cart avec les options de livraison
  };

  const handleCheckoutWithOptions = (
    deliveryOption: "delivery" | "pickup",
    deliveryFee: number,
    orderId: string,
    deliveryAddress?: Address
  ) => {
    // Stocker les options de livraison pour le checkout
    setDeliveryOptions({
      option: deliveryOption,
      address: deliveryAddress,
      fee: deliveryFee,
      orderId: orderId,
    });

    setCurrentPage("checkout");
  };

  const [deliveryOptions, setDeliveryOptions] = useState<{
    option: "delivery" | "pickup";
    address?: Address;
    fee: number;
    orderId?: string;
  }>({
    option: "delivery",
    fee: 7.99,
  });

  const handleOrderPlaced = (order: Order) => {
    setCurrentOrder(order);
    setCartItems([]);
    setPaymentSucceeded(true);
    setCurrentPage("order-history");
  };

  const handleBackToCart = () => {
    setPaymentSucceeded(false);
    setCurrentPage("cart");
  };

  const handleLegalPageOpen = (
    type: "privacy" | "terms" | "refund" | "legal"
  ) => {
    setLegalPageType(type);
    setCurrentPage("legal");
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setCurrentPage("order-detail");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        cartItemsCount={getCartItemsCount()}
        user={user}
        onOpenAuth={onOpenAuth}
        onLogout={onLogout}
      />

      {/* Guest Benefits Banner */}
      {!user && currentPage === "menu" && (
        <div className="bg-orange-50 border-b border-orange-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-3 text-sm text-orange-700">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs">✨</span>
                  </div>
                  <div>
                    <span className="font-semibold">
                      {t("banner.benefits.title")}
                    </span>
                    <div className="hidden md:block text-xs text-orange-600 mt-1">
                      {t("banner.benefits.features")}
                    </div>
                  </div>
                </div>
              </div>
              {/* <button 
                onClick={() => onOpenAuth('register')}
                className="btn-primary text-sm py-2"
              >
                {t('banner.benefits.create')}
              </button> */}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentPage === "menu" && <Menu onAddToCart={addToCart} />}

        {currentPage === "cart" && (
          <Cart
            items={cartItems}
            onRemoveItem={removeFromCart}
            onUpdateQuantity={updateQuantity}
            onCheckout={handleCheckoutWithOptions}
            totalPrice={getTotalPrice()}
          />
        )}

        {currentPage === "checkout" && (
          <Checkout
            cartItems={cartItems}
            orderId={deliveryOptions.orderId!}
            totalPrice={getTotalPrice()}
            deliveryOption={deliveryOptions.option}
            deliveryAddress={deliveryOptions.address}
            deliveryFee={deliveryOptions.fee}
            onOrderPlaced={handleOrderPlaced}
            onBack={handleBackToCart}
          />
        )}

        {currentPage === "tracking" && currentOrder && (
          <OrderTracking order={currentOrder} />
        )}

        {currentPage === "service" && <CustomerService />}

        {currentPage === "legal" && (
          <LegalPages
            locationSettings={locationSettings}
            pageType={legalPageType}
            onBack={() => setCurrentPage("menu")}
          />
        )}

        {currentPage === "profile" && (
          <Profile setCurrentPage={setCurrentPage} onLogout={onLogout} />
        )}

        {currentPage === "order-history" && (
          <OrderHistory onViewOrder={handleViewOrder} />
        )}

        {currentPage === "update-profile" && <UpdateProfile />}

        {currentPage === "order-detail" && selectedOrder && (
          <OrderDetail
            order={selectedOrder}
            onBack={() => setCurrentPage("order-history")}
          />
        )}

        {currentPage === "about" && <About />}
      </main>

      {/* Scroll to Top Button */}
      <ScrollToTop />

      {/* Dev Tools - Visible seulement en développement */}
      <DevTools />

      {/* Footer */}
      <Footer
        onLegalPageOpen={handleLegalPageOpen}
        setCurrentPage={setCurrentPage}
        locationSettings={locationSettings}
      />
    </div>
  );
};

export default MainApp;
